import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useMediaQuery } from "@mui/material";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  LinearProgress,
  IconButton,
  Avatar,
  Popover,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import BusinessIcon from '@mui/icons-material/Business';
import { useLazyQuery, gql } from "@apollo/client";
import Nav from "../common/Nav.jsx";
import FAQSection from "../common/FAQSection.jsx";
import { GRAPHQL_ENDPOINTS } from "../../config/api";
import { BUSINESS_CONSTANTS } from "../../config/businessConstants";
import { getAuthToken, getCsrfTokenSync } from "../../utils/auth";
import { getCsrfTokenKeyByEnv } from "../../utils/csrf";
import { apiFetch } from "../../utils/api";
import { getStatusDisplay } from "../../utils/statusUtils";
import { useAuth } from "../common/AuthContext.jsx";


// ticketDetailsCurrentPage
        // hasNext
        // currentPage
        // totalCount
        // ticketDetailsTotalPages
        // page

const GET_GRIEVANCE_TRANSACTION_AND_TICKET_DETAILS = gql`
  query getGrievanceTransactionAndTicketDetails($input: GrievanceTransactionAndTicketDetailsFilter) {
    getGrievanceTransactionAndTicketDetails(input: $input) {
      status
      message
      data {
        statusKey
        statusMsg
        ticketDetailsCurrentPage
        pageSize
        hasNext
        page
        currentPage
        totalCount
        ticketDetailsTotalPages
        transactions {
          id
          date
          mode
          email
          amount
          refundAmount
          status
          merchantId
        }
        ticketDetails {
          issueId
          id
          transactionId
          description
        }
        customer {
          id
          name
          mobileNo
          email
        }
      }
    }
  }
`;

const GET_GRIEVANCE_TRANSACTION_DETAILS = gql`
  query getGrievanceTransactionDetails($input: GrievanceTransactionDetailsFilter) {
    getGrievanceTransactionDetails(input: $input) {
      status
      message
      data {
        date
        amount
        pgRefId
        refundDetails
        transactionId
        mode
        statusMsg
        statusKey
        currency
        settlementAmount
        captureDate
        ticketId
        customerDetails
        status
      }
    }
  }
`;

// Add a helper function for formatting
function formatDateTime(dateObj) {
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const min = String(dateObj.getMinutes()).padStart(2, '0');
  const ss = String(dateObj.getSeconds()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd} ${hh}:${min}:${ss}`;
}

const TransactionHistory = () => {
  const csrfTokenKey = getCsrfTokenKeyByEnv();
  const location = useLocation();
  const { clearAuthState, logoutUser, isAuthenticated } = useAuth();
  const isMobile = useMediaQuery("(max-width:600px)");

  // Phone number from navigation state, falling back to sessionStorage on refresh
  const phoneNumber = location.state?.phoneNumber;
  const transactionDataFromState = location.state?.transactionData;

  // Detect if this is a paymentId flow from CustomerForm
  const isPaymentIdFlow = sessionStorage.getItem('paymentIdFlow') === 'true';

  // Extract payment ID from transactionDataFromState if available
  const paymentIdFromState = sessionStorage.getItem('lastPaymentId') || 
                            transactionDataFromState?.transactions?.[0]?.id || '';

  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateFilterRef = useRef(null);
  // Remove paymentType from state and filter logic
  // const [paymentType, setPaymentType] = useState("all");
  const [merchantName, setMerchantName] = useState("all");
  const [paymentAmount, setPaymentAmount] = useState("all");
  const [page, setPage] = useState(1);
  const [transactions, setTransactions] = useState(transactionDataFromState?.transactions || []);
  const [ticketDetails, setTicketDetails] = useState(transactionDataFromState?.ticketDetails || []);
  const [customer, setCustomer] = useState(transactionDataFromState?.customer || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(transactionDataFromState?.totalCount || 0);
  const [hasMorePages, setHasMorePages] = useState(true);
  // Remove pageSize state and dropdown, set as constant
  const pageSize = BUSINESS_CONSTANTS.PAGINATION.DEFAULT_PAGE_SIZE;
  const navigate = useNavigate();
  // Loader for navigation to transaction details
  const [navLoading, setNavLoading] = useState(false);
// const [filterStatusMsg, setFilterStatusMsg] = useState(""); // Remove this line

  const [getTransactionDetails, { loading: mutationLoading }] = useLazyQuery(
    GET_GRIEVANCE_TRANSACTION_AND_TICKET_DETAILS,
    { fetchPolicy: 'network-only' }
  );

  const [getTransactionDetailsByPaymentId] = useLazyQuery(GET_GRIEVANCE_TRANSACTION_DETAILS);

  const paymentModeOptions = [
  { label: 'All', value: 'all' },
  { label: 'Credit Card', value: 'CC' },
  { label: 'Debit Card', value: 'DC' },
  { label: 'Netbanking', value: 'NB' },
  { label: 'UPI Collect', value: 'UPI_COLLECT' },
  { label: 'UPI QR', value: 'UPI_QR' },
  { label: 'UPI Intent', value: 'UPI_INTENT' },
  { label: 'Wallet', value: 'PPI' },
  { label: 'EMI (Debit Card)', value: 'EMI_DC' },
  { label: 'EMI (Credit Card)', value: 'EMI_CC' },
  { label: 'EMI (Cardless)', value: 'EMI_CARDLESS' },
  { label: 'No Cost EMI (Debit Card)', value: 'NCEMI_DC' },
  { label: 'No Cost EMI (Credit Card)', value: 'NCEMI_CC' },
  { label: 'No Cost EMI (Cardless)', value: 'NCEMI_CARDLESS' },
  { label: 'Netbanking Emandate', value: 'NB_EMANDATE' },
  { label: 'Credit Card SI', value: 'CC_SI' },
  { label: 'Debit Card SI', value: 'DC_SI' },
  { label: 'UPI Emandate', value: 'UPI_EMANDATE' },
  { label: 'Aadhar Emandate', value: 'AADHAR_EMANDATE' },
  { label: 'Debit Card Emandate', value: 'DC_EMANDATE' },
  { label: 'UPI QR OTS', value: 'UPI_QR_OTS' },
  { label: 'UPI Intent OTS', value: 'UPI_INTENT_OTS' },
  { label: 'UPI Collect OTS', value: 'UPI_COLLECT_OTS' },
  { label: 'UPI QR Autopay', value: 'UPI_QR_AUTOPAY' },
  { label: 'UPI Collect Autopay', value: 'UPI_COLLECT_AUTOPAY' },
  { label: 'UPI Intent Autopay', value: 'UPI_INTENT_AUTOPAY' },
  { label: 'Credit Card SI', value: 'CC_SI' },
  { label: 'Debit Card SI', value: 'DC_SI' },
  { label: 'Credit Card SI Int', value: 'CC_SI_INT' },
  { label: 'Debit Card SI Int', value: 'DC_SI_INT' },
  { label: 'Credit Card Int', value: 'CC_INT' },
  { label: 'Debit Card Int', value: 'DC_INT' },
];

  const [paymentMode, setPaymentMode] = useState('all');

  /**
   * @param {object} [opts]
   * @param {string} [opts.dateFilter] — override current date filter for this request
   * @param {string} [opts.paymentMode] — override payment mode for this request
   * @param {number} [opts.page] — override page for this request
   * @param {boolean} [opts.force] — fetch even when `transactionData` exists in navigation state
   */
  const fetchTransactions = async (opts = {}) => {
    const activeDate = opts.dateFilter !== undefined ? opts.dateFilter : dateFilter;
    const activePm = opts.paymentMode !== undefined ? opts.paymentMode : paymentMode;
    const activePage = opts.page !== undefined ? opts.page : page;

    if (transactionDataFromState && !opts.force) return; // Don't fetch if we already have static data
    setLoading(true);
    setError(null);
    
    try {
      // Calculate date range based on activeDate
      const today = new Date();
      
      // Get today's date in local timezone (YYYY-MM-DD format)
      const todayString = today.toLocaleDateString('en-CA'); // Returns YYYY-MM-DD format
      
      let startDate, endDate;

      if (activeDate === 'today') {
        // Today: start and end are today
        startDate = todayString + " 00:00:00";
        endDate = todayString + " 23:59:59";
      } else if (activeDate === 'this_week') {
        // This week: start is last Sunday
        const dayOfWeek = today.getDay();
        const daysDiff = today.getDate() - dayOfWeek; // Sunday is day 0, so no adjustment needed
        const sunday = new Date(today.getFullYear(), today.getMonth(), daysDiff);
        const sundayString = sunday.toLocaleDateString('en-CA');
        startDate = sundayString + " 00:00:00";
        endDate = todayString + " 23:59:59";
      } else if (activeDate === 'this_month') {
        // This month: start is first day of month
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const firstDayString = firstDay.toLocaleDateString('en-CA');
        startDate = firstDayString + " 00:00:00";
        endDate = todayString + " 23:59:59";
      } else if (dateFilter === 'last_2_months') {
        const pastDate = new Date(today);
        pastDate.setMonth(pastDate.getMonth() - 2);
        startDate = pastDate.toLocaleDateString('en-CA') + " 00:00:00";
        endDate = todayString + " 23:59:59";
      } else if (dateFilter === 'custom' && customDateRange[0] && customDateRange[1]) {
        startDate = customDateRange[0].toLocaleDateString('en-CA') + " 00:00:00";
        endDate = customDateRange[1].toLocaleDateString('en-CA') + " 23:59:59";
      } else {
        // All: last 59 days, endDate is today
        const pastDate = new Date(today);
        pastDate.setDate(pastDate.getDate() - 59);
        const pastDateString = pastDate.toLocaleDateString('en-CA');
        startDate = pastDateString + " 00:00:00";
        endDate = todayString + " 23:59:59";
      }

      console.log('Date range:', { startDate, endDate, dateFilter: activeDate, todayString });

      const input = {
        startDate: startDate,
        endDate: endDate,
        page: activePage,
        pageSize: pageSize,
      };
      if (activePm !== 'all') {
        input.paymentMode = activePm;
        input.transactionType = 'payments';
      }

      const { data } = await getTransactionDetails({
        variables: {
          input
        }
      });

      if (data.getGrievanceTransactionAndTicketDetails.status === "SUCCESS" || 
          data.getGrievanceTransactionAndTicketDetails.status === "OK") {
        const apiData = data.getGrievanceTransactionAndTicketDetails.data;
        const transactionData = apiData.transactions || [];
        setTransactions(transactionData);
        setCustomer(apiData.customer || null);
        setTicketDetails(apiData.ticketDetails || []);
        console.log("Transactions from API:", transactionData); // Debug log
        // Use currentPage and totalCount from API for pagination
        setPage(apiData.currentPage || 1);
        setTotalCount(apiData.totalCount || transactionData.length);
        setHasMorePages(apiData.hasNext || false);
        // Show success toast with statusMsg
        const statusMsg = apiData?.statusMsg || "Transactions fetched successfully";
      } else {
        const errorMsg = data.getGrievanceTransactionAndTicketDetails.message || "Failed to fetch transactions";
        setError(errorMsg);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
      const errorMsg = "Failed to fetch transaction data";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setDateFilter('all');
    setPaymentMode('all');
    setMerchantName('all');
    setPaymentAmount('all');
    setPage(1);
    setError(null);
    setShowDatePicker(false);
    if (location.state?.transactionData) {
      navigate(location.pathname, {
        replace: true,
        state: { ...location.state, transactionData: null },
      });
    }
    fetchTransactions({
      dateFilter: 'all',
      paymentMode: 'all',
      page: 1,
      force: true,
    });
  };

  // Add search by paymentId function
  const searchByPaymentId = async (paymentId) => {
    setLoading(true);
    setError(null);
    
    try {
      const today = new Date();
      const endDate = today.toISOString().slice(0, 10) + " 23:59:59";
      const startDateObj = new Date(today);
      startDateObj.setMonth(startDateObj.getMonth() - BUSINESS_CONSTANTS.DATE_RANGES.DEFAULT_MONTHS_BACK);
      const startDate = startDateObj.toISOString().slice(0, 10) + " 00:00:00";
      
      // Use proper authentication token instead of hardcoded bearer token
      const bearerToken = sessionStorage.getItem('bearerToken') || localStorage.getItem('accessToken') || '';
      const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'Authorization': `Bearer ${bearerToken}`,
          [csrfTokenKey]: getCsrfTokenSync()
        },
        credentials: 'include', // Include cookies in the request
        body: JSON.stringify({
          query: `query getGrievanceTransactionAndTicketDetails($input: GrievanceTransactionAndTicketDetailsFilter) {\n    getGrievanceTransactionAndTicketDetails(input: $input) {\n        status\n        message\n        data{\n                statusKey\n                statusMsg\n                ticketDetailsCurrentPage\n    pageSize\n    hasNext\n    page\n    currentPage\n    totalCount\n    ticketDetailsTotalPages\n                transactions{\n                    id\n                    date\n                    mode\n                    email\n                    amount\n                    refundAmount\n                    status\n                    merchantId\n                }\n                ticketDetails{\n                    issueId\n                    id\n                    transactionId\n                    description\n                }\n                customer{\n                    id\n                    name\n                    mobileNo\n                    email\n                }\n            }\n        \n        \n    }\n}`,
          variables: {
            input: {
              paymentId: paymentId.trim(),
              startDate,
              endDate,
              page: 1,
              pageSize: BUSINESS_CONSTANTS.PAGINATION.DEFAULT_PAGE_SIZE,
            }
          }
        })
      });
      
      const data = await response.json();
      if (data.data && data.data.getGrievanceTransactionAndTicketDetails && 
          (data.data.getGrievanceTransactionAndTicketDetails.status === "OK" || 
           data.data.getGrievanceTransactionAndTicketDetails.status === "SUCCESS")) {
        const apiData = data.data.getGrievanceTransactionAndTicketDetails.data;
        const transactionList = apiData.transactions || [];
        setTransactions(transactionList);
        console.log("Transactions from API:", transactionList); // Debug log
        setTicketDetails(apiData.ticketDetails || []);
        setCustomer(apiData.customer || null);
        // For search by transactionId, show all transactions regardless of totalCount
        setTotalCount(transactionList.length);
        setHasMorePages(false); // No pagination for search results
        setPage(1);
      } else {
        setTransactions([]);
        setTicketDetails([]);
        setCustomer(null);
        setTotalCount(0);
        setHasMorePages(false);
        const errorMsg = data.data?.getGrievanceTransactionAndTicketDetails?.message || "No results found";
      }
    } catch (error) {
      console.error("Error searching by Payment ID:", error);
      setTransactions([]);
      setTicketDetails([]);
      setCustomer(null);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Add search handler
  const handleSearch = () => {
    if (searchQuery.trim()) {
      searchByPaymentId(searchQuery);
    } else {
      fetchTransactions();
    }
  };

  React.useEffect(() => {
    if (transactionDataFromState) return;
    setPage(1); // Reset to first page when filters change
  }, [phoneNumber, paymentAmount, dateFilter, paymentMode, transactionDataFromState]);

  // Add useEffect to trigger API call when paymentMode changes
  React.useEffect(() => {
    if (transactionDataFromState) return;
    setPage(1);
    fetchTransactions();
    // eslint-disable-next-line
  }, [paymentMode]);

  React.useEffect(() => {
    if (transactionDataFromState) return;
    if (dateFilter === 'custom') return; // custom range fires via DatePicker onChange
    fetchTransactions();
  }, [page, phoneNumber, paymentAmount, dateFilter, paymentMode, pageSize, transactionDataFromState]);

  const handlePageClick = (pageNum) => {
    setPage(pageNum);
    // fetchTransactions will be triggered by useEffect
  };

  const filtersActive = dateFilter !== "all" || paymentMode !== "all";

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showNextPagination =
    transactions.length > 0 && (totalPages > 1 || hasMorePages);

  const handleLogout = async () => {
    try {
      await logoutUser();
    } finally {
      sessionStorage.removeItem('paymentIdFlow');
      sessionStorage.removeItem('lastPaymentId');
      sessionStorage.removeItem('bearerToken');
      navigate("/", { state: { bypassAuth: true } });
    }
  };

  // Loader overlay
  const showLoader = loading || mutationLoading;

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #E9F7FF, #DDEFFF, #FFFFFF)",
        minHeight: "100vh",
      }}
    >
      <Nav />

      <Box
        sx={{
          px: { xs: "16px", md: "70px" },
          pt: { xs: "80px", md: "32px" },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "20px", md: "32px" },
              lineHeight: "42px",
              color: "#253858",
            }}
          >
            Payment status
          </Box>
          {(isAuthenticated || getAuthToken()) && (
            <Button
              variant="outlined"
              onClick={handleLogout}
              sx={{
                height: 36,
                px: 2.5,
                borderRadius: '38px',
                textTransform: 'none',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '24px',
                letterSpacing: '0.0016em',
                borderColor: '#B3BAC5',
                color: '#253858',
                backgroundColor: '#FFFFFF',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: '#0065FF',
                  backgroundColor: '#F9FAFB',
                  color: '#253858',
                },
              }}
            >
              Log out
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            mt: 0,
            mb: 2, // Add margin below subheading
            fontWeight: 400,
            lineHeight: '24px',
            fontSize: '16px',
            letterSpacing: '0.0016em',
            color: '#25385899',
            maxWidth: '100%',
            position: 'relative', // Ensure stacking context
            zIndex: 1000, // High z-index for parent
          }}
        >
          <span>View transaction history of the last 2 months for</span>
          <span style={{ fontWeight: 700, color: '#253858', marginLeft: 4 }}>
            {customer?.mobileNo || phoneNumber || 'your account'}
          </span>
        </Box>
        <Box sx={{ mt: 0, mb: 3 }}>
          {/* Filters and search bar in the same row on desktop */}
          <Box
            sx={{
              display: { xs: 'block', md: 'flex' },
              alignItems: { md: 'center' },
              gap: { md: 2 },
              mb: 2,
              width: '100%',
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                overflowX: { xs: 'auto', md: 'visible' },
                '&::-webkit-scrollbar': { display: 'none' },
                mb: { xs: 2, md: 0 },
                width: { xs: '100%', md: 'auto' },
                flex: { md: 1 },
              }}
            >
              <Grid item xs={6} sm={2} md={2} ref={dateFilterRef}>
                <FormControl fullWidth>
                  <Select
                    value={dateFilter}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDateFilter(val);
                      setPage(1);
                      if (val === 'custom' && !(customDateRange[0] && customDateRange[1])) {
                        // Only reset range if no range was previously selected
                        setCustomDateRange([null, null]);
                      }
                      if (val === 'custom') {
                        setShowDatePicker(true);
                      }
                    }}
                    renderValue={(val) => {
                      if (val === 'custom' && customDateRange[0] && customDateRange[1]) {
                        const fmt = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                        return `${fmt(customDateRange[0])} – ${fmt(customDateRange[1])}`;
                      }
                      const labels = { all: 'Select Date', today: 'Today', this_week: 'This Week', this_month: 'This Month', last_2_months: 'Last 2 Months', custom: 'Custom Date' };
                      return labels[val] || val;
                    }}
                    sx={{
                      borderRadius: "38px",
                      border: "1px solid #B3BAC5",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "24px",
                      height: "36px",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    <MenuItem value="all">Select Date</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="this_week">This Week</MenuItem>
                    <MenuItem value="this_month">This Month</MenuItem>
                    <MenuItem value="last_2_months">Last 2 Months</MenuItem>
                    <MenuItem value="custom" onClick={() => {
                      // Ensure calendar opens even if 'custom' is already selected
                      setShowDatePicker(true);
                    }}>Custom Date</MenuItem>
                  </Select>
                </FormControl>
                <Popover
                  open={showDatePicker}
                  anchorEl={dateFilterRef.current}
                  onClose={() => setShowDatePicker(false)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <Box sx={{ p: 2 }}>
                    <DatePicker
                      selectsRange
                      startDate={customDateRange[0]}
                      endDate={customDateRange[1]}
                      maxDate={new Date()}
                      onChange={(update) => {
                        setCustomDateRange(update);
                        if (update[0] && update[1]) {
                          setShowDatePicker(false);
                          // Fetch with the selected range directly — avoids stale closure
                          const start = update[0].toLocaleDateString('en-CA') + " 00:00:00";
                          const end = update[1].toLocaleDateString('en-CA') + " 23:59:59";
                          setLoading(true);
                          setError(null);
                          const input = {
                            startDate: start,
                            endDate: end,
                            page: 1,
                            pageSize,
                          };
                          if (paymentMode !== 'all') {
                            input.paymentMode = paymentMode;
                            input.transactionType = 'payments';
                          }
                          getTransactionDetails({ variables: { input } }).then(({ data }) => {
                            if (data?.getGrievanceTransactionAndTicketDetails?.status === "SUCCESS" ||
                                data?.getGrievanceTransactionAndTicketDetails?.status === "OK") {
                              const apiData = data.getGrievanceTransactionAndTicketDetails.data;
                              setTransactions(apiData.transactions || []);
                              setPage(apiData.currentPage || 1);
                              setTotalCount(apiData.totalCount || 0);
                              setHasMorePages(apiData.hasNext || false);
                            } else {
                              setError(data?.getGrievanceTransactionAndTicketDetails?.message || "Failed to fetch transactions");
                            }
                          }).catch(() => setError("Failed to fetch transaction data")).finally(() => setLoading(false));
                        }
                      }}
                      inline
                    />
                  </Box>
                </Popover>
              </Grid>

              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth>
                  <Select
                    value={paymentMode}
                    onChange={(e) => {
                      setPaymentMode(e.target.value);
                      setPage(1);
                    }}
                    sx={{
                      borderRadius: "38px",
                      border: "1px solid #B3BAC5",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "24px",
                      height: "36px",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    {paymentModeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {filtersActive && (
                <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      borderRadius: "38px",
                      border: "1px solid #FF5630",
                      fontSize: "13px",
                      fontWeight: 500,
                      height: "36px",
                      color: "#FF5630",
                      backgroundColor: "#FFF4F2",
                      textTransform: "none",
                      px: 2,
                      whiteSpace: "nowrap",
                      "&:hover": { backgroundColor: "#FFE8E3", borderColor: "#FF5630" },
                    }}
                  >
                    Reset Filter 
                  </Button>
                </Grid>
              )}
            </Grid>
            {/* Hide search bar if paymentIdFlow is true */}
            {!isPaymentIdFlow && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: { xs: '100%', sm: '420px', md: '420px' },
                  height: '48px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  pl: '16px',
                  pr: '4px',
                  gap: 0.5,
                  ml: { md: 2 },
                  mt: { xs: 2, md: 0 },
                  flexShrink: 0,
                }}
              >
                <TextField
                  placeholder="Search by payment ID"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '16.41px',
                    letterSpacing: '0.016em',
                    '& fieldset': { border: 'none' },
                  }}
                />
                {searchQuery && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                      fetchTransactions({ force: true });
                    }}
                    sx={{
                      flexShrink: 0,
                      color: '#8993A4',
                      p: '4px',
                      '&:hover': { color: 'red', backgroundColor: 'transparent', fontSize: 25 },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                )}
                <Button
                  sx={{
                    backgroundColor: '#E8F1FF',
                    color: '#0065FF',
                    px: 2,
                    borderRadius: 2,
                    height: '40px',
                    minWidth: '82px',
                    flexShrink: 0,
                    textTransform: 'capitalize',
                  }}
                  startIcon={<SearchIcon sx={{ color: '#0065FF' }} />}
                  onClick={handleSearch}
                >
                  {isMobile ? '' : 'Search'}
                </Button>
              </Box>
            )}
          </Box>
          {/* Remove filterStatusMsg display from the UI */}
          {/* {filterStatusMsg && (
            <Typography
              sx={{
                mt: 1,
                mb: 2,
                color: "#0065FF",
                fontWeight: 500,
                fontSize: "16px",
                textAlign: "center"
              }}
            >
              {filterStatusMsg}
            </Typography>
          )} */}
        </Box>
        {showLoader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', py: 10 }}>
            <CircularProgress size={48} color="primary" />
          </Box>
        ) : error ? (
          <Box
            sx={{
              maxWidth: 480,
              mx: 'auto',
              mt: 10,
              mb: 14,
              p: { xs: 3, sm: 5 },
              background: 'linear-gradient(135deg, #F8F9FA 60%, #E3F0FF 100%)',
              border: '1.5px solid #E1E5E9',
              borderRadius: '20px',
              boxShadow: '0 4px 24px 0 rgba(0,0,0,0.07)',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#FDEDED',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                boxShadow: '0 4px 12px rgba(220, 53, 69, 0.15)',
              }}
            >
              {error.toLowerCase().includes('network') || error.toLowerCase().includes('fetch') ? (
                <WifiOffIcon sx={{ fontSize: 40, color: '#DC3545' }} />
              ) : (
                <ErrorOutlineIcon sx={{ fontSize: 40, color: '#DC3545' }} />
              )}
            </Box>
            
            <Typography sx={{ fontWeight: 800, fontSize: 26, color: '#253858', mb: 1, letterSpacing: 0.2 }}>
              {error.toLowerCase().includes('network') || error.toLowerCase().includes('fetch') 
                ? 'Connection Error' 
                : 'Something went wrong'}
            </Typography>
            
            <Box sx={{ width: '100%', my: 2 }}>
              <hr style={{ border: 'none', borderTop: '1.5px solid #E1E5E9', margin: 0 }} />
            </Box>
            
            <Typography sx={{ color: '#253858', fontSize: 18, mb: 2, fontWeight: 500 }}>
              {error.toLowerCase().includes('network') || error.toLowerCase().includes('fetch')
                ? 'Unable to connect to our servers. Please check your internet connection and try again.'
                : error}
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => {
                // Clear authentication state
                clearAuthState();
                
                // Clear sessionStorage for transactionId flow
                sessionStorage.removeItem('paymentIdFlow');
                sessionStorage.removeItem('lastPaymentId');
                sessionStorage.removeItem('bearerToken');
                
                // Clear localStorage
                localStorage.removeItem('accessToken');
                
                // Redirect to login page
                window.location.href = '/';
              }}
              sx={{
                background: 'linear-gradient(90deg, #3965FB 60%, #4F8FFF 100%)',
                color: '#fff',
                fontWeight: 700,
                fontSize: 16,
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                boxShadow: '0 2px 8px 0 rgba(57,101,251,0.10)',
                mb: 3,
                textTransform: 'capitalize',
                '&:hover': {
                  background: 'linear-gradient(90deg, #335ecc 60%, #3965FB 100%)',
                },
              }}
            >
              Try Again
            </Button>
            
            <Typography sx={{ color: '#25385899', fontSize: 16, mb: 3, fontWeight: 400 }}>
              Still having trouble? Please write to us at
            </Typography>
            
            <a href="mailto:grievancesupport@pbpay.com" style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  display: 'inline-block',
                  background: 'linear-gradient(90deg, #3965FB 60%, #4F8FFF 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 16,
                  px: 3,
                  py: 1.2,
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px 0 rgba(57,101,251,0.10)',
                  mb: 2,
                  transition: 'background 0.2s',
                  ':hover': {
                    background: 'linear-gradient(90deg, #335ecc 60%, #3965FB 100%)',
                  },
                }}
              >
                grievancesupport@pbpay.com
              </Box>
            </a>
            
            <Typography sx={{ color: '#25385899', fontSize: 15, mt: 1 }}>
              Detailing your concerns along with details of the transaction.
            </Typography>
          </Box>
        ) : transactions.length > 0 ? (
          <>
            {isMobile ? (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 2,
                    width: "100%",
                    minHeight: "400px",
                  }}
                >
                  {transactions.map((transaction) => (
                    <Box
                      key={transaction.id}
                      sx={{ cursor: 'pointer', textDecoration: 'none' }}
                      onClick={() => {
                        setNavLoading(true);
                        setTimeout(() => {
                          navigate(`/transaction-details/${transaction.id}`, {
                            state: { merchantId: transaction.merchantId }
                          });
                        }, 300); // short delay to show loader
                      }}
                    >
                      <Card
                        sx={{
                          width: "100%",
                          borderRadius: "8px",
                          boxShadow: "none",
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "center",
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: "#E8F1FF",
                                  color: "#0065FF",
                                }}
                              >
                                <BusinessIcon sx={{ fontSize: 18 }} />
                              </Avatar>
                              <Typography
                                sx={{
                                  fontSize: "16px",
                                  fontWeight: 400,
                                  lineHeight: "24px",
                                  color: "#253858",
                                }}
                              >
                                #{transaction.id}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 0.5,
                              }}
                            >
                              <Chip
                                label={getStatusDisplay(transaction.status).label}
                                sx={{
                                  height: "22px",
                                  borderRadius: "100px",
                                  backgroundColor: getStatusDisplay(transaction.status).backgroundColor,
                                  color: getStatusDisplay(transaction.status).color,
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  letterSpacing: "0.0016em",
                                }}
                              />
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "16px",
                                  color: "#000000",
                                  margin: "auto",
                                }}
                              >
                                ₹{(Number(transaction.amount) / 100).toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                        <Box
                          sx={{
                            backgroundColor: "#F4F5F7",
                            borderTop: "1px dashed #DFE1E6",
                            p: 1.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "14px",
                              lineHeight: "20px",
                              color: "#253858",
                            }}
                          >
                            {/* TODO: replace merchantId with merchantName once API exposes it */}
                            {transaction.merchantId}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "20px",
                              color: "#253858",
                              opacity: 0.6,
                            }}
                          >
                            {formatDateTime(new Date((transaction.date || '').replace(' ', 'T')))}
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 3,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    disabled={page === 1}
                    onClick={() => handlePageClick(1)}
                    aria-label="First page"
                    sx={{
                      backgroundColor: "#FFFFFF",
                      color: "#0065FF",
                      minWidth: 44,
                      height: 32,
                      fontSize: "12px",
                      lineHeight: "14.06px",
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                    }}
                  >
                    {'|<'}
                  </Button>
                  <Button
                    disabled={page === 1}
                    onClick={() => handlePageClick(page - 1)}
                    sx={{
                      backgroundColor: "#FFFFFF",
                      color: "#0065FF",
                      minWidth: 60,
                      height: 32,
                      fontSize: "12px",
                      lineHeight: "14.06px",
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                    }}
                  >
                    Prev
                  </Button>
                  <Button
                    disabled={!hasMorePages && page === totalPages}
                    onClick={() => handlePageClick(page + 1)}
                    sx={{
                      backgroundColor: "#FFFFFF",
                      color: "#0065FF",
                      minWidth: 60,
                      height: 32,
                      fontSize: "12px",
                      lineHeight: "14.06px",
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                    }}
                  >
                    Next
                  </Button>
                  <Button
                    disabled={page === totalPages || (!hasMorePages && page >= totalPages)}
                    onClick={() => handlePageClick(totalPages)}
                    aria-label="Last page"
                    sx={{
                      backgroundColor: "#FFFFFF",
                      color: "#0065FF",
                      minWidth: 44,
                      height: 32,
                      fontSize: "12px",
                      lineHeight: "14.06px",
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                    }}
                  >
                    {'>|'}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                    gap: 3,
                  }}
                >
                  {transactions.map((transaction) => (
                    <Box
                      key={transaction.id}
                      sx={{ cursor: 'pointer', textDecoration: 'none' }}
                      onClick={() => {
                        setNavLoading(true);
                        setTimeout(() => {
                          navigate(`/transaction-details/${transaction.id}`, {
                            state: { merchantId: transaction.merchantId }
                          });
                        }, 300); // short delay to show loader
                      }}
                    >
                      <Card
                        sx={{
                          width: "100%",
                          borderRadius: "20px",
                          boxShadow: "none",
                          height: "auto",
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: "#E8F1FF",
                                  color: "#0065FF",
                                }}
                              >
                                <BusinessIcon sx={{ fontSize: 18 }} />
                              </Avatar>
                              <Typography
                                sx={{
                                  fontSize: "16px",
                                  fontWeight: 400,
                                  lineHeight: "24px",
                                  color: "#253858",
                                  maxWidth: "300px",
                                }}
                              >
                                 #{transaction.id}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 0.5,
                              }}
                            >
                              <Chip
                                label={getStatusDisplay(transaction.status).label}
                                sx={{
                                  height: "22px",
                                  borderRadius: "100px",
                                  backgroundColor: getStatusDisplay(transaction.status).backgroundColor,
                                  color: getStatusDisplay(transaction.status).color,
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  letterSpacing: "0.0016em",
                                }}
                              />
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "18px",
                                  lineHeight: "26px",
                                  color: "#000000",
                                  margin: "auto",
                                }}
                              >
                                ₹{(Number(transaction.amount) / 100).toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                        <Box
                          sx={{
                            backgroundColor: "#F4F5F7",
                            borderTop: "1px dashed #DFE1E6",
                            p: 1.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "14px",
                              lineHeight: "20px",
                              color: "#253858",
                            }}
                          >
                            {/* TODO: replace merchantId with merchantName once API exposes it */}
                            {transaction.merchantId}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "20px",
                              color: "#253858",
                              opacity: 0.6,
                            }}
                          >
                            {formatDateTime(new Date((transaction.date || '').replace(' ', 'T')))}
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 3,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    disabled={page === 1}
                    onClick={() => handlePageClick(1)}
                    aria-label="First page"
                    sx={{
                      backgroundColor: "#FFFFFF",
                      color: "#0065FF",
                      minWidth: 44,
                      height: 32,
                      fontSize: "12px",
                      lineHeight: "14.06px",
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                    }}
                  >
                    {'|<'}
                  </Button>
                  <Button
                    disabled={page === 1}
                    onClick={() => handlePageClick(page - 1)}
                    sx={{
                      backgroundColor: "#FFFFFF",
                      color: "#0065FF",
                      minWidth: 60,
                      height: 32,
                      fontSize: "12px",
                      lineHeight: "14.06px",
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                    }}
                  >
                    Prev
                  </Button>
                  <Button
                    disabled={!hasMorePages && page === totalPages}
                    onClick={() => handlePageClick(page + 1)}
                    sx={{
                      backgroundColor: "#FFFFFF",
                      color: "#0065FF",
                      minWidth: 60,
                      height: 32,
                      fontSize: "12px",
                      lineHeight: "14.06px",
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                    }}
                  >
                    Next
                  </Button>
                  <Button
                    disabled={page === totalPages || (!hasMorePages && page >= totalPages)}
                    onClick={() => handlePageClick(totalPages)}
                    aria-label="Last page"
                    sx={{
                      backgroundColor: "#FFFFFF",
                      color: "#0065FF",
                      minWidth: 44,
                      height: 32,
                      fontSize: "12px",
                      lineHeight: "14.06px",
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                    }}
                  >
                    {'>|'}
                  </Button>
                </Box>
              </>
            )}
          </>
        ) : (
          <Box
            sx={{
              maxWidth: 480,
              mx: 'auto',
              mt: 10,
              mb: 14,
              p: { xs: 3, sm: 5 },
              background: 'linear-gradient(135deg, #F8F9FA 60%, #E3F0FF 100%)',
              border: '1.5px solid #E1E5E9',
              borderRadius: '20px',
              boxShadow: '0 4px 24px 0 rgba(0,0,0,0.07)',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#FFF3CD',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                boxShadow: '0 4px 12px rgba(255, 193, 7, 0.15)',
              }}
            >
              <SentimentDissatisfiedIcon sx={{ fontSize: 40, color: '#856404' }} />
            </Box>
            
            <Typography sx={{ fontWeight: 800, fontSize: 26, color: '#253858', mb: 1, letterSpacing: 0.2 }}>
              No Transactions Found
            </Typography>
            
            <Box sx={{ width: '100%', my: 2 }}>
              <hr style={{ border: 'none', borderTop: '1.5px solid #E1E5E9', margin: 0 }} />
            </Box>
            
            <Typography sx={{ color: '#253858', fontSize: 18, mb: 2, fontWeight: 500 }}>
              We are unable to find any transactions linked to the mobile number provided by you.
            </Typography>
            
            <Typography sx={{ color: '#25385899', fontSize: 16, mb: 3, fontWeight: 400 }}>
              Don't worry! Please write to us at
            </Typography>
            
            <a href="mailto:grievancesupport@pbpay.com" style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  display: 'inline-block',
                  background: 'linear-gradient(90deg, #3965FB 60%, #4F8FFF 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 16,
                  px: 3,
                  py: 1.2,
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px 0 rgba(57,101,251,0.10)',
                  mb: 2,
                  transition: 'background 0.2s',
                  ':hover': {
                    background: 'linear-gradient(90deg, #335ecc 60%, #3965FB 100%)',
                  },
                }}
              >
                grievancesupport@pbpay.com
              </Box>
            </a>
            
            <Typography sx={{ color: '#25385899', fontSize: 15, mt: 1 }}>
              Detailing your concerns along with details of the transaction.
            </Typography>
          </Box>
        )}
      </Box>

      <FAQSection />
      {/* Loader overlay for navigation to details */}
      {navLoading && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(255,255,255,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={64} color="primary" />
        </Box>
      )}
    </Box>
  );
};

export default TransactionHistory;